import { Upload, List, Button as AntButton } from "antd";
import React, { useState } from "react";
import Button from "../Helpers/Button/Button";
import {
	UploadOutlined,
	CloseOutlined,
	PaperClipOutlined,
} from "@ant-design/icons";
import trans from "../../utils";
import { toast } from "react-toastify";

export default function AntUpload({
	item,
	preview,
	addData,
	setSubmitData,
	submitData,
	readOnly,
	isEditing = false,
	editValues = null,
	view,
}) {
	const [value, setValue] = useState("");

	const MAX_FILES = 5;
	const MAX_FILE_SIZE_MB = 5;
	const MAX_TOTAL_MB = 25;

	const rawValue = addData
		? submitData?.find((i) => i.key === item.key)?.value
		: (item?.value ?? value);

	let currentValue;
	if (Array.isArray(rawValue)) {
		currentValue = rawValue;
	} else if (rawValue === "[]" || rawValue == null) {
		currentValue = [];
	} else {
		currentValue = [rawValue];
	}

	const getVisibleValue = () => {
		if (!isEditing || !addData) return currentValue;
		const removedAttachments = addData.values.removed_attachments || [];
		return currentValue.filter((file) => {
			const isFileObject = file instanceof File;
			const isStringAttachment = typeof file === "string";
			const isObjectAttachment =
				!isFileObject && (file?.url || file?.filename || file?.id);
			const isExistingAttachment = isStringAttachment || isObjectAttachment;
			if (isExistingAttachment) {
				const fileIdentifier = isStringAttachment
					? file
					: file?.filename || file?.url || file?.name || file?.id;
				return !removedAttachments.includes(fileIdentifier);
			}
			return true;
		});
	};

	const visibleValue = getVisibleValue();

	// ✅ Returns true if valid, false if not
	const validateNewFiles = (existingFiles, newFiles) => {
		// Check individual file size first
		for (const file of newFiles) {
			if (file.size / 1024 / 1024 > MAX_FILE_SIZE_MB) {
				toast.info(trans(`Each file must not exceed ${MAX_FILE_SIZE_MB}MB.`), {
					autoClose: 2000,
					rtl: localStorage.getItem("lang") === "ar",
				});
				return false;
			}
		}

		// Check total file count
		const combined = [...existingFiles, ...newFiles];
		if (combined.length > MAX_FILES) {
			toast.info(trans(`Maximum ${MAX_FILES} files are allowed.`), {
				autoClose: 2000,
				rtl: localStorage.getItem("lang") === "ar",
			});
			return false;
		}

		// Check total size
		const totalMB =
			combined.reduce((sum, f) => sum + (f.size || 0), 0) / 1024 / 1024;
		if (totalMB > MAX_TOTAL_MB) {
			toast.info(
				trans(`Total size of all files must not exceed ${MAX_TOTAL_MB}MB.`),
				{ autoClose: 2000, rtl: localStorage.getItem("lang") === "ar" },
			);
			return false;
		}

		return true;
	};

	const updateFiles = (merged) => {
		setValue(merged);
		setSubmitData((prev) => {
			const filtered = prev?.filter((i) => i.key !== item.key) || [];
			return [
				...filtered,
				{ key: item.key, name: item.name, title: item.title, value: merged },
			];
		});
	};

	return (
		<div style={item?.css}>
			<label
				htmlFor={item?.key}
				className="form-label d-flex align-items-center me-2"
				style={{
					color: item?.css?.color,
					fontFamily: item?.css?.fontFamily,
					fontSize: item?.css?.fontSize,
					fontWeight: item?.css?.fontWeight,
				}}
			>
				<div>{item?.title}</div>
				{item?.required?.value && value === "" && (
					<div className="text-danger ms-2">*</div>
				)}
			</label>

			<Upload
				id={item?.key}
				disabled={readOnly}
				showUploadList={false}
				multiple={true}
				beforeUpload={(file, fileList) => {
					// ✅ Only run once on the last file of the batch
					if (file !== fileList[fileList.length - 1]) return false;

					const isValid = validateNewFiles(visibleValue, fileList);
					if (!isValid) return Upload.LIST_IGNORE;

					return false; // prevent auto-upload
				}}
				onChange={({ fileList }) => {
					// ✅ Extract raw File objects from this batch
					const newFiles = fileList
						.map((f) => f.originFileObj || f)
						.filter((f) => f instanceof File);

					console.log("=== onChange fired ===");
					console.log("fileList from antd:", fileList);
					console.log("newFiles extracted:", newFiles);
					console.log("currentValue at time of onChange:", currentValue);
					console.log(
						"previousNewFiles:",
						currentValue.filter((f) => f instanceof File),
					);
					console.log("rawValue:", rawValue);
					console.log("submitData:", submitData);
					if (!newFiles.length) return;

					// ✅ Carry forward previously added File objects
					const previousNewFiles = currentValue.filter(
						(f) => f instanceof File,
					);

					// ✅ For edit mode, keep existing server attachments not removed
					const removedAttachments = addData?.values?.removed_attachments || [];
					const existingKept = currentValue.filter((file) => {
						const isFile = file instanceof File;
						const isExisting =
							typeof file === "string" ||
							(!isFile && (file?.url || file?.filename || file?.id));
						if (!isExisting) return false;
						const id =
							typeof file === "string"
								? file
								: file?.filename || file?.url || file?.name || file?.id;
						return !removedAttachments.includes(id);
					});

					// ✅ Deduplicate new files by name to avoid adding the same file twice
					const existingFileNames = new Set(
						previousNewFiles.map((f) => f.name),
					);
					const dedupedNewFiles = newFiles.filter(
						(f) => !existingFileNames.has(f.name),
					);

					const merged = isEditing
						? [...existingKept, ...previousNewFiles, ...dedupedNewFiles]
						: [...previousNewFiles, ...dedupedNewFiles];

					if (addData) {
						updateFiles(merged);
						addData.setFieldTouched(`fields[${item.key}].value`, true);
					} else {
						setValue(merged);
					}
				}}
			>
				<Button icon={<UploadOutlined />}>{trans("Click to Upload")}</Button>
			</Upload>

			{((addData && visibleValue?.length > 0) || item?.value?.length > 0) && (
				<div className="mt-3">
					<List
						size="small"
						bordered
						dataSource={visibleValue || item?.value}
						renderItem={(file, idx) => {
							const isFileObject = file instanceof File;
							const isStringAttachment = typeof file === "string";
							const isObjectAttachment =
								!isFileObject && (file?.url || file?.filename || file?.id);
							const isExistingAttachment =
								isStringAttachment || isObjectAttachment;

							const displayName = isStringAttachment
								? file
								: file?.name ||
									file?.filename ||
									file?.original_name ||
									(file?.url ? file.url.split("/").pop() : file);

							const fileSize = file?.size
								? `${(file.size / 1024).toFixed(2)} KB`
								: file?.sizeMB
									? `${file.sizeMB} MB`
									: "";

							const fileUrl = file?.url || null;

							return (
								<List.Item
									key={idx}
									actions={[
										<AntButton
											type="text"
											danger
											size="small"
											icon={<CloseOutlined />}
											disabled={view}
											onClick={() => {
												if (isExistingAttachment && isEditing && addData) {
													const fileIdentifier = isStringAttachment
														? file
														: file?.filename ||
															file?.url ||
															file?.name ||
															file?.id;
													const currentRemoved =
														addData.values.removed_attachments || [];
													if (!currentRemoved.includes(fileIdentifier)) {
														addData.setFieldValue("removed_attachments", [
															...currentRemoved,
															fileIdentifier,
														]);
													}
												} else {
													// ✅ Remove by reference for File objects
													const updated = currentValue.filter((f, index) => {
														if (f instanceof File && file instanceof File) {
															return f !== file;
														}
														return index !== idx;
													});
													updateFiles(updated);
												}
											}}
										>
											{trans("Remove")}
										</AntButton>,
									]}
								>
									<List.Item.Meta
										avatar={<PaperClipOutlined />}
										title={
											fileUrl ? (
												<a
													href={fileUrl}
													target="_blank"
													rel="noopener noreferrer"
												>
													{displayName}
												</a>
											) : (
												<span>{displayName}</span>
											)
										}
										description={fileSize}
									/>
								</List.Item>
							);
						}}
					/>
				</div>
			)}

			{preview !== true && (
				<>
					{item?.required?.value && visibleValue?.length === 0 && (
						<div className="text-danger mt-1">
							{trans(item?.required?.validationMessage)}
						</div>
					)}
				</>
			)}
		</div>
	);
}
